import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Free Audit + Sample Reel",
  "Starter Package",
  "Growth Package",
  "Authority Package",
  "Creative Sprint",
];

const EMPTY = { name: "", agency: "", email: "", whatsapp: "", social_handle: "" };

export const LeadModal = ({ open, onOpenChange, interest, onInterestChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest });
      toast.success("Audit request received!", {
        description: "Our team will reach out within 24 hours with your free audit and sample reel.",
      });
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#0b1426] border border-white/10 focus:border-[#fba31b]/60 focus:outline-none focus:ring-1 focus:ring-[#fba31b]/40 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-modal" className="bg-[#12203c] border border-[#fba31b]/25 text-white sm:max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">
            Book Your <span className="text-[#fba31b]">Free Audit + Sample Reel</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Tell us where to send your audit. Our team responds within 24 hours, Dubai time.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="lead-name-input" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="lead-agency-input" required placeholder="Agency / brokerage *" value={form.agency} onChange={set("agency")} className={inputCls} />
          </div>
          <input data-testid="lead-email-input" required type="email" placeholder="Work email *" value={form.email} onChange={set("email")} className={inputCls} />
          <input data-testid="lead-whatsapp-input" required placeholder="WhatsApp number *" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
          <input data-testid="lead-social-input" placeholder="Instagram / social handle (optional)" value={form.social_handle} onChange={set("social_handle")} className={inputCls} />

          <Select value={interest} onValueChange={onInterestChange}>
            <SelectTrigger data-testid="lead-interest-select" className="w-full rounded-xl bg-[#0b1426] border-white/10 text-sm text-white px-4 py-3 h-auto">
              <SelectValue placeholder="I'm interested in" />
            </SelectTrigger>
            <SelectContent className="bg-[#12203c] border-white/10 text-white">
              {INTERESTS.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`lead-interest-${opt.toLowerCase().replace(/[\s+]/g, "-")}`}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            data-testid="lead-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#fba31b] hover:bg-[#e59312] disabled:opacity-60 text-[#12203c] font-bold px-6 py-4 rounded-full text-sm transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending..." : "Claim My Free Audit"}
          </button>
          <p className="text-center text-xs text-slate-500">No spam. No obligation. Your details stay private.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
